import {
  SETTINGS_PREMIUM_SUPERWALL_PLACEMENT,
  useUpgradeToPremium,
} from "@-/dev.improve.haniverse.components.superwall";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useTimeUntilNextDailyNewGraphemes } from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import { useDailyNewCardsSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.daily-new-cards.navigator";
import type { LearnSummaryDueReviewCard } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.navigator";
import { View } from "@-/com.npmjs.react-native";
import type { ReactNode } from "react";
import { LearnSummaryReadyView } from "#learn-summary-ready-view";

type LearnSummaryDailyLimitViewProps = {
  dueReviewCardList: readonly LearnSummaryDueReviewCard[];
  dueReviewLabel: string | null;
  /** The limit that was reached, and whether it is the free tier's cap. */
  limit: { isFreeTierCap: boolean; limit: number };
};

/**
 * The learn summary once a learner has introduced today's allowance of new graphemes AND reviewed
 * everything due. A session never ends on the limit itself: at the limit the due scan revives the
 * cards just learned as reviews, so the learner always arrives here from a review, and the card
 * congratulates them on getting through their reviews before saying when the next new card comes.
 *
 * Its bottom button is what lifts the limit: Upgrade to Premium for a FREE learner held to the
 * free tier's cap, or Change Daily New Cards for a learner who reached the limit they set
 * themselves. A swipe presses that button too, and the card stays: there is no next card to go to.
 */
export function LearnSummaryDailyLimitView({ limit, ...props }: LearnSummaryDailyLimitViewProps) {
  return limit.isFreeTierCap ? (
    <LearnSummaryFreeTierDailyLimitView {...props} limit={limit.limit} />
  ) : (
    <LearnSummaryOwnDailyLimitView {...props} limit={limit.limit} />
  );
}

function LearnSummaryFreeTierDailyLimitView({
  limit,
  ...props
}: Omit<LearnSummaryDailyLimitViewProps, "limit"> & { limit: number }) {
  // The explicit-upgrade placement, shared with Settings and the free-tier completion screen. The
  // learn-gate placement is a different campaign and may have no paywall assigned to it.
  const { errorMessage, isLoading, isPaywallPresented, openUpgradeToPremium } = useUpgradeToPremium(
    SETTINGS_PREMIUM_SUPERWALL_PLACEMENT,
    "summary_daily_limit",
  );
  return (
    <LearnSummaryReadyView
      {...props}
      cta={{
        icon: "FiStar",
        keepsCard: true,
        label: "Upgrade to Premium",
        onPress: isPaywallPresented ? null : openUpgradeToPremium,
      }}
      ctaPendingLabel={isLoading ? "Loading plans…" : null}
      notice={
        <LearnSummaryDailyLimitCongratulation limit={limit}>
          {errorMessage === null ? null : (
            <Text
              accessibilityRole="alert"
              className="text-center font-sans text-[13px]"
              colorVariant="muted"
            >
              {errorMessage}
            </Text>
          )}
          <Text className="text-center font-sans text-[13px]" colorVariant="muted">
            Premium lifts the daily limit: learn as many new characters as you like, plus every
            level and every mnemonic.
          </Text>
        </LearnSummaryDailyLimitCongratulation>
      }
      showReviewOptions
    />
  );
}

function LearnSummaryOwnDailyLimitView({
  limit,
  ...props
}: Omit<LearnSummaryDailyLimitViewProps, "limit"> & { limit: number }) {
  const dailyNewCardsPageNavigator = useDailyNewCardsSettingsPageNavigator();
  return (
    <LearnSummaryReadyView
      {...props}
      cta={{
        icon: "MdSettings",
        keepsCard: true,
        label: "Change Daily New Cards",
        onPress: () => dailyNewCardsPageNavigator.push.mutate(),
      }}
      notice={
        <LearnSummaryDailyLimitCongratulation limit={limit}>
          <Text className="text-center font-sans text-[13px]" colorVariant="muted">
            {`Daily New Cards is set to ${limit.toLocaleString()}.`}
          </Text>
        </LearnSummaryDailyLimitCongratulation>
      }
      showReviewOptions
    />
  );
}

/** Centered in the card, like "No reviews due! 🎉" on any other day with nothing due. */
function LearnSummaryDailyLimitCongratulation({
  children,
  limit,
}: {
  children: ReactNode;
  limit: number;
}) {
  const timeUntilNextNewCard = useTimeUntilNextDailyNewGraphemes();
  return (
    <View className="items-center gap-4 pt-2" testID="learn-summary-daily-limit-notice">
      <View className="items-center gap-2">
        <Text className="text-center font-sans text-[20px] font-semibold" colorVariant="default">
          Congrats on getting through your reviews! 🎉
        </Text>
        <Text className="text-center font-sans text-[17px]" colorVariant="muted">
          {`You've learned today's ${limit.toLocaleString()} new ${limit === 1 ? "character" : "characters"}. Your next new card unlocks in ${timeUntilNextNewCard}.`}
        </Text>
      </View>
      <View className="items-center gap-2">{children}</View>
    </View>
  );
}
